import React, { useState, useEffect } from 'react';
import { slpRecords, organizations, venues, studentsWithNumbers } from '../../Data/studentLearningProfiles';
import { slpTypes, eventTypes } from '../../Data/studentLearningProfiles/slpConfig';
import { teachers } from '../../Data/teachers';
import SLPEventForm from './SLPEventForm';
import SLPEventDetails from './SLPEventDetails';
import SLPStudentSelector from './SLPStudentSelector';

const SLPEventPage = ({ selectedDate, onBack }) => {
  const [currentView, setCurrentView] = useState('list'); // list, form, details, students
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formData, setFormData] = useState({
    date: selectedDate || new Date().toISOString().split('T')[0],
    slpType: 'ole',
    eventType: 'academic',
    title: '',
    organization: '',
    venue: '',
    timeFrom: '09:00',
    timeTo: '12:00',
    duration: 3,
    teachersInCharge: [],
    students: [],
    level: 'school',
    role: 'participant',
    remarks: '',
    achievementLevel: ''
  });
  const [dateRecords, setDateRecords] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedDate && slpRecords) {
      const records = slpRecords.filter(event => event.date === selectedDate);
      setDateRecords(records);
    }
  }, [selectedDate, slpRecords]);

  const handleCreateNew = () => {
    setCurrentView('form');
    setSelectedRecord(null);
    setFormData({
      ...formData,
      date: selectedDate,
      slpNumber: `SLP-${selectedDate.replace(/-/g, '')}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`
    });
  };

  const handleEditRecord = (record) => {
    setSelectedRecord(record);
    setFormData({
      ...record,
      teachersInCharge: record.teachersInCharge || [],
      students: record.students || []
    });
    setCurrentView('form');
  };

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setCurrentView('details');
  };

  const handleFormSubmit = (data) => {
    const validation = validateForm(data);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    try {
      if (selectedRecord) {
        // Update existing record
        console.log('Updating record:', data);
      } else {
        // Create new record
        const newRecord = {
          ...data,
          id: Math.random().toString(36).substr(2, 9), // Simple ID generation
          date: selectedDate,
          slpNumber: `SLP-${selectedDate.replace(/-/g, '')}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`
        };
        console.log('Created new record:', newRecord);
        setDateRecords([...dateRecords, newRecord]);
      }
      
      setCurrentView('list');
      setErrors({});
    } catch (error) {
      setErrors({ general: error.message });
    }
  };

  const validateForm = (data) => {
    const errors = {};
    
    if (!data.title?.trim()) errors.title = '請輸入活動標題';
    if (!data.organization) errors.organization = '請選擇機構';
    if (!data.venue) errors.venue = '請選擇地點';
    if (!data.students?.length) errors.students = '請至少選擇一名學生';
    if (data.duration <= 0) errors.duration = '活動時間必須大於0';
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  const handleConvertToAward = (oleRecord) => {
    const awardData = {
      ...oleRecord,
      id: undefined,
      slpNumber: `SLP-${oleRecord.date.replace(/-/g, '')}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`,
      slpType: 'awards',
      title: `${oleRecord.title} - 獎項`,
      achievementLevel: 'merit',
      remarks: `從OLE活動轉換: ${oleRecord.remarks || ''}`
    };
    
    setFormData(awardData);
    setSelectedRecord(null);
    setCurrentView('form');
  };

  const getSlpTypeDisplay = (type) => slpTypes?.[type]?.name || type;
  const getEventTypeDisplay = (type) => eventTypes?.[type]?.name || type;
  const getOrganizationName = (orgId) => organizations?.find(org => org.id === orgId)?.name || orgId;
  const getVenueName = (venueId) => venues?.find(venue => venue.id === venueId)?.name || venueId;

  if (currentView === 'form') {
    return (
      <SLPEventForm
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleFormSubmit}
        onCancel={() => setCurrentView('list')}
        errors={errors}
        isEdit={!!selectedRecord}
      />
    );
  }

  if (currentView === 'details') {
    return (
      <SLPEventDetails
        record={selectedRecord}
        onBack={() => setCurrentView('list')}
        onEdit={() => handleEditRecord(selectedRecord)}
        onConvertToAward={handleConvertToAward}
      />
    );
  }

  if (currentView === 'students') {
    return (
      <SLPStudentSelector
        selectedStudents={formData.students}
        onStudentsSelected={(students) => {
          setFormData({ ...formData, students });
          setCurrentView('form');
        }}
        onCancel={() => setCurrentView('form')}
      />
    );
  }

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#f8f9fa', 
      minHeight: '100vh',
      fontFamily: 'Arial, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <button 
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            marginRight: '15px',
            color: '#007bff'
          }}
        >
          ← 
        </button>
        <div>
          <h2 style={{ margin: 0, color: '#333' }}>學生學習概覽 (SLP)</h2>
          <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
            日期: {selectedDate} | 記錄數: {dateRecords.length}
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={handleCreateNew}
          style={{
            padding: '12px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          ➕ 新增 SLP 記錄
        </button>
        
        <select
          style={{
            padding: '12px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '14px'
          }}
          onChange={(e) => {
            if (e.target.value && slpRecords) {
              const filtered = slpRecords.filter(record => record.slpType === e.target.value);
              setDateRecords(filtered);
            } else if (slpRecords) {
              setDateRecords(slpRecords.filter(event => event.date === selectedDate));
            }
          }}
        >
          <option value="">所有類型</option>
          {slpTypes && Object.entries(slpTypes).map(([key, type]) => (
            <option key={key} value={key}>{type.name}</option>
          ))}
        </select>
      </div>

      {/* Records List */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {dateRecords.length === 0 ? (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: '#666'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>📅</div>
            <h3>此日期暫無 SLP 記錄</h3>
            <p>點擊上方「新增 SLP 記錄」按鈕開始記錄學生活動</p>
          </div>
        ) : (
          <div>
            {/* Table Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '120px 80px 150px 200px 150px 100px 80px',
              gap: '10px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #ddd',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              <div>SLP編號</div>
              <div>類型</div>
              <div>活動類型</div>
              <div>標題</div>
              <div>機構</div>
              <div>學生數</div>
              <div>操作</div>
            </div>

            {/* Table Rows */}
            {dateRecords.map((record) => (
              <div
                key={record.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px 80px 150px 200px 150px 100px 80px',
                  gap: '10px',
                  padding: '15px',
                  borderBottom: '1px solid #eee',
                  fontSize: '14px',
                  alignItems: 'center'
                }}
              >
                <div style={{ 
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  {record.slpNumber}
                </div>
                
                <div>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    backgroundColor: slpTypes[record.slpType]?.color || '#gray',
                    color: 'white'
                  }}>
                    {getSlpTypeDisplay(record.slpType)}
                  </span>
                </div>
                
                <div style={{ color: '#666' }}>
                  {getEventTypeDisplay(record.eventType)}
                </div>
                
                <div style={{ 
                  fontWeight: 'bold',
                  color: '#333',
                  cursor: 'pointer'
                }}
                onClick={() => handleViewDetails(record)}>
                  {record.title}
                </div>
                
                <div style={{ color: '#666' }}>
                  {getOrganizationName(record.organization)}
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <span style={{
                    padding: '2px 6px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '10px',
                    fontSize: '12px',
                    color: '#1976d2'
                  }}>
                    {record.students?.length || 0}
                  </span>
                </div>
                
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button
                    onClick={() => handleViewDetails(record)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    查看
                  </button>
                  
                  <button
                    onClick={() => handleEditRecord(record)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: '#ffc107',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    編輯
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary Statistics */}
      {dateRecords.length > 0 && (
        <div style={{
          marginTop: '20px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>今日統計</h3>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '15px'
          }}>
            {slpTypes && Object.entries(slpTypes).map(([key, type]) => {
              const count = dateRecords.filter(r => r.slpType === key).length;
              return (
                <div key={key} style={{
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  textAlign: 'center',
                  border: `2px solid ${type.color}20`
                }}>
                  <div style={{ fontSize: '24px', marginBottom: '5px' }}>
                    {type.icon}
                  </div>
                  <div style={{ 
                    fontSize: '20px', 
                    fontWeight: 'bold',
                    color: type.color 
                  }}>
                    {count}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {type.name}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SLPEventPage; 